import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, MapPin, Printer, Loader2, Globe, ShieldCheck, BadgeCheck } from 'lucide-react';
import aarogyaLogo from '../assets/Aarogya india hindia updated logo.svg';
import { formatCurrency } from '../utils/helpers';
import { message } from 'antd';
import { useTheme } from '../context/ThemeContext';

const getMockInvoiceData = (id) => ({
  invoiceNumber: id || '123876',
  invoiceDate: '23/03/2024 05:09 PM',
  customer: { name: 'Mukesh Kumar Sharma', phone: '7065088499', address: 'B-429, Raykola High School North 24 Parganas WEST BENGAL - 743424' },
  products: [{ sno: 1, name: 'AAROGYANIGHT GOLD', unitPrice: 499.00, quantity: 1, discount: 0.00, total: 499.00, hsnCode: '3004' }],
  subtotal: 499.00,
  taxableAmount: 499.00,
  gstRate: 18,
  toPay: 499.00
});

const getMockCompanyData = () => ({
  name: 'Aarogya India',
  gstin: 'XXXXXXX733A1ZJ',
  fssai: 'XXXXXXXXXXX175',
  address: 'GT 64, Sector 70, Noida, Uttar Pradesh 201301',
  phone: '7838082254',
  email: 'aarogyaindia123@yopmail.com',
  tagline: 'पहला सुख - निरोगी काया'
});

function InvoiceDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';

  const [invoiceData, setInvoiceData] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setInvoiceData(getMockInvoiceData(id));
    setCompanyData(getMockCompanyData());
    setLoading(false);
  }, [id]);

  const handlePrint = () => window.print();

  if (loading || !invoiceData) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center ${isDark ? 'bg-[#0F172A]' : 'bg-white'}`}>
        <Loader2 className="w-12 h-12 animate-spin mb-4" style={{ color: currentTheme.color }} />
        <p className={`font-black uppercase tracking-widest text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Synchronizing Ledger...</p>
      </div>
    );
  }

  const gstValue = (invoiceData.taxableAmount || 0) * ((invoiceData.gstRate || 18) / 100);
  const cgst = gstValue / 2;
  const sgst = gstValue / 2;

  return (
    <div className={`min-h-screen font-sans transition-colors duration-500 ${isDark ? 'bg-[#0F172A]' : 'bg-[#F8FAFC]'}`}>
      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          body { background: white !important; }
          .no-print { display: none !important; }
          .invoice-main-container { margin: 0 !important; padding: 1.5cm !important; box-shadow: none !important; border: none !important; width: 100% !important; height: auto !important; position: absolute; top: 0; left: 0; color: #000 !important; }
          .invoice-main-container * { color: #000 !important; border-color: #eee !important; }
        }
      `}</style>

      {/* Action Bar */}
      <div className="max-w-5xl mx-auto pt-8 px-6 no-print">
        <div className={`flex items-center justify-between mb-10 p-6 rounded-[32px] border transition-all shadow-2xl ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
          <button onClick={() => navigate(-1)} className={`p-4 rounded-2xl transition-all shadow-lg active:scale-90 ${isDark ? 'bg-white/5 text-white hover:bg-white/10' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
            <ArrowLeft size={24} style={{ color: currentTheme.color }} />
          </button>
          <div className="flex items-center gap-6">
            <div className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] shadow-inner ${isDark ? 'bg-white/5 text-green-400' : 'bg-green-50 text-green-600'}`}>STATUS: AUTHENTICATED</div>
            <button onClick={handlePrint} className="flex items-center gap-3 px-8 py-4 rounded-2xl text-white font-black uppercase tracking-[0.2em] text-[10px] shadow-2xl active:scale-95 transition-all" style={{ backgroundColor: currentTheme.color }}>
              <Printer size={18} />
              <span>Generate Hardcopy</span>
            </button>
          </div>
        </div>
      </div>

      {/* Invoice Container */}
      <div className={`invoice-main-container max-w-5xl mx-auto mb-20 p-16 rounded-[48px] shadow-2xl border relative overflow-hidden transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
        
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-3" style={{ backgroundColor: currentTheme.color }}></div>
        <div className="absolute top-0 right-0 w-64 h-64 blur-[120px] rounded-full -mr-32 -mt-32 opacity-10" style={{ backgroundColor: currentTheme.color }}></div>

        {/* Header Section */}
        <div className="flex justify-between items-start mb-16 relative z-10">
          <div>
            <img src={aarogyaLogo} alt="Logo" className={`h-20 mb-6 object-contain ${isDark ? 'brightness-200' : ''}`} />
            <h1 className={`text-3xl font-black uppercase tracking-[0.1em] ${isDark ? 'text-white' : 'text-gray-900'}`}>{companyData.name}</h1>
            <p className="font-black text-xs mt-1 uppercase tracking-[0.3em]" style={{ color: currentTheme.color }}>{companyData.tagline}</p>

            <div className="mt-8 space-y-2 text-[10px] font-black uppercase tracking-widest text-gray-500">
              <p>TAX IDENTIFIER (GSTIN): <span className={isDark ? 'text-gray-300' : 'text-gray-900'}>{companyData.gstin}</span></p>
              <p>FOOD SAFETY (FSSAI): <span className={isDark ? 'text-gray-300' : 'text-gray-900'}>{companyData.fssai}</span></p>
            </div>
          </div>

          <div className="text-right">
            <div className={`inline-block px-10 py-4 rounded-3xl font-black text-2xl mb-8 tracking-[0.2em] shadow-2xl ${isDark ? 'bg-white/5 text-white border border-white/10' : 'bg-gray-900 text-white'}`}>
              TAX INVOICE
            </div>
            <div className="space-y-4">
              <div className="flex justify-end items-center gap-4">
                <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Reg ID</span>
                <span className={`text-lg font-black tabular-nums ${isDark ? 'text-white' : 'text-gray-900'}`}>#{invoiceData.invoiceNumber}</span>
              </div>
              <div className="flex justify-end items-center gap-4">
                <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Timestamp</span>
                <span className={`text-sm font-black tabular-nums ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>{invoiceData.invoiceDate}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Addresses */}
        <div className="grid grid-cols-2 gap-16 mb-16 py-12 border-y border-white/5 relative z-10">
          <div className={`p-8 rounded-[40px] border ${isDark ? 'bg-white/2 border-white/5' : 'bg-gray-50 border-gray-100 shadow-inner'}`}>
            <h3 className="text-[10px] font-black uppercase tracking-[0.3em] mb-6" style={{ color: currentTheme.color }}>Consignee (Bill To)</h3>
            <p className={`text-2xl font-black mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{invoiceData.customer?.name}</p>
            <div className="space-y-4 text-xs font-black text-gray-500 uppercase leading-relaxed tracking-wider">
              <div className="flex items-start gap-3">
                <MapPin size={16} style={{ color: currentTheme.color }} className="flex-shrink-0 mt-0.5" />
                <span>{invoiceData.customer?.address}</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={16} style={{ color: currentTheme.color }} className="flex-shrink-0" />
                <span>{invoiceData.customer?.phone}</span>
              </div>
            </div>
          </div>

          <div className="text-right flex flex-col items-end p-8">
            <h3 className="text-[10px] font-black uppercase tracking-[0.3em] mb-6" style={{ color: currentTheme.color }}>Provider Context</h3>
            <p className={`text-sm font-black uppercase leading-relaxed max-w-xs mb-6 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{companyData.address}</p>
            <div className="space-y-4 text-xs font-black text-gray-500 uppercase tracking-widest">
              <p className="flex items-center justify-end gap-3">
                <span>{companyData.phone}</span>
                <Phone size={16} style={{ color: currentTheme.color }} />
              </p>
              <p className="flex items-center justify-end gap-3">
                <span>{companyData.email}</span>
                <Mail size={16} style={{ color: currentTheme.color }} />
              </p>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="mb-16 relative z-10">
          <table className="w-full">
            <thead>
              <tr className={`border-b-2 ${isDark ? 'border-white/10' : 'border-gray-900'}`}>
                <th className="py-6 text-left text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] w-16">ID</th>
                <th className="py-6 text-left text-[10px] font-black text-gray-500 uppercase tracking-[0.3em]">Item Specification</th>
                <th className="py-6 text-center text-[10px] font-black text-gray-500 uppercase tracking-[0.3em]">Valuation</th>
                <th className="py-6 text-center text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] w-24">Units</th>
                <th className="py-6 text-right text-[10px] font-black text-gray-500 uppercase tracking-[0.3em]">Aggregate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {(invoiceData.products || []).map((item, idx) => (
                <tr key={idx} className="group">
                  <td className="py-8 text-sm font-black text-gray-500">0{idx + 1}</td>
                  <td className="py-8">
                    <p className={`text-lg font-black uppercase tracking-wider mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.name}</p>
                    <span className={`text-[9px] font-black px-3 py-1 rounded-lg uppercase tracking-widest ${isDark ? 'bg-white/5 text-gray-500' : 'bg-gray-100 text-gray-400'}`}>HSN CLASSIFICATION: {item.hsnCode}</span>
                  </td>
                  <td className={`py-8 text-center text-sm font-black tabular-nums ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{formatCurrency(item.unitPrice)}</td>
                  <td className={`py-8 text-center text-sm font-black tabular-nums ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.quantity}</td>
                  <td className={`py-8 text-right text-lg font-black tabular-nums ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(item.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals Section */}
        <div className="flex justify-between items-start mb-20 relative z-10">
          <div className="w-1/2">
            <h4 className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] mb-6">Terms of Engagement</h4>
            <div className={`text-[10px] font-black space-y-3 uppercase tracking-widest leading-relaxed ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              <p>• Final transaction. No reversals permitted.</p>
              <p>• Wellness outcomes subject to protocol adherence.</p>
              <p>• Disputations subject to local jurisdictional review.</p>
            </div>
          </div>

          <div className={`w-1/3 p-8 rounded-[40px] space-y-6 ${isDark ? 'bg-white/2' : 'bg-gray-50 shadow-inner'}`}>
            <div className="flex justify-between items-center text-[10px] font-black text-gray-500 uppercase tracking-widest">
              <span>Sub-ledger</span>
              <span className={`text-sm tabular-nums ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(invoiceData.subtotal)}</span>
            </div>
            <div className="flex justify-between items-center text-[10px] font-black text-gray-500 uppercase tracking-widest">
              <span>Federal Tax (9.0%)</span>
              <span className={`text-sm tabular-nums ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(cgst)}</span>
            </div>
            <div className="flex justify-between items-center text-[10px] font-black text-gray-500 uppercase tracking-widest pb-6 border-b border-white/10">
              <span>Regional Tax (9.0%)</span>
              <span className={`text-sm tabular-nums ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(sgst)}</span>
            </div>
            <div className="flex justify-between items-center pt-2">
              <span className="text-[11px] font-black uppercase tracking-[0.2em]" style={{ color: currentTheme.color }}>Net Payable</span>
              <span className={`text-3xl font-black tabular-nums ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(invoiceData.toPay)}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-end pt-16 border-t border-white/5 relative z-10">
          <div className="text-center">
            <div className="mb-6 h-28 flex flex-col justify-center items-center opacity-30">
              <Globe className="w-12 h-12 mb-4" style={{ color: currentTheme.color }} />
              <p className="text-[9px] font-black uppercase tracking-[0.5em]">AAROGYAINDIA.COM</p>
            </div>
          </div>

          <div className="text-right">
            <div className="mb-8 h-20 flex flex-col items-end justify-center">
               <div className={`w-48 h-1 ${isDark ? 'bg-white/10' : 'bg-gray-200'} rounded-full`}></div>
            </div>
            <p className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] mb-1">Authorized Registry Official</p>
            <p className={`text-xs font-black uppercase tracking-widest ${isDark ? 'text-white' : 'text-gray-900'}`}>FOR {companyData.name}</p>
          </div>
        </div>

        <div className="mt-16 text-center text-[9px] font-black uppercase tracking-[0.6em] opacity-20">
          System Authenticated Ledger • Thank you
        </div>

      </div>
    </div>
  );
}

export default InvoiceDetails;
